import React, { useEffect, useState, useRef } from "react";
import useApi from "../../../../CustomHooks/useApi";
import { useSelector } from "react-redux";
import { Fingerprint, Loader2, CheckCircle2 } from "lucide-react";
import VimoEkycComponent from "../../VimoAEPS/VimoEkycComponent";
import VimoRegistration from "../../VimoAEPS/VimoRegistration";
import VimoTwoFactorAuth from "../../VimoAEPS/VimoTwoFactorAuth";

function VimoEkycFlow({ onBack, onSuccess }) {
  const [phase, setPhase] = useState("registration");
  return phase === "registration" ? (
    <VimoRegistration
      onRegistered={() => {}}
      onOtpVerified={() => setPhase("biometric")}
      onBack={onBack}
    />
  ) : (
    <VimoEkycComponent
      onBack={() => setPhase("registration")}
      onSuccess={onSuccess}
    />
  );
}

const bankMappings = [
  { short: "SBI",      full: "State Bank of India" },
  { short: "PNB",      full: "Punjab National Bank" },
  { short: "BOB",      full: "Bank Of Baroda" },
  { short: "BOI",      full: "Bank Of India" },
  { short: "UBI",      full: "Union Bank of India" },
  { short: "INDIAN",   full: "Indian Bank" },
  { short: "CBI",      full: "Central Bank of India" },
  { short: "CANARA",   full: "Canara Bank" },
  { short: "BOM",      full: "Bank Of Maharashtra" },
  { short: "PSB",      full: "Punjab And Sind Bank" },
  { short: "IOB",      full: "Indian Overseas Bank" },
  { short: "UCO",      full: "UCO Bank" },
  { short: "AIRTEL",   full: "Airtel Payments Bank" },
  { short: "IPPB",     full: "India Post Payments Bank" },
  { short: "HDFC",     full: "HDFC Bank" },
  { short: "ICICI",    full: "ICICI Bank" },
  { short: "AXIS",     full: "Axis Bank" },
  { short: "IDBI",     full: "IDBI Bank" },
  { short: "INDUSIND", full: "IndusInd Bank" },
  { short: "KOTAK",    full: "Kotak Mahindra Bank" },
];

const BANK_COLORS = [
  "bg-blue-700", "bg-red-600",    "bg-orange-600", "bg-indigo-900",
  "bg-rose-600", "bg-blue-800",   "bg-blue-600",   "bg-teal-600",
  "bg-sky-600",  "bg-orange-500", "bg-indigo-700", "bg-green-600",
  "bg-purple-600","bg-cyan-600",  "bg-red-500",    "bg-orange-700",
  "bg-pink-600", "bg-violet-600", "bg-emerald-600","bg-fuchsia-600",
];

export default function VimoApForm2({
  onSubmit,
  loading,
  previousData,
  processing,
  clearFormTrigger,
}) {
  const username = useSelector((state) => state.auth?.employeeDetails?.username);

  const { callApi: getBankIIN }         = useApi("/aeps/vimopay/bank-iin", "POST");
  const { callApi: getOnboardingStatus } = useApi("/aeps/vimopay/onboarding-status", "GET");

  const [banks, setBanks]                       = useState([]);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [onboardingLoading, setOnboardingLoading] = useState(true);
  const [ekycDone, setEkycDone] = useState(false);

  // txnType is fixed to "AP" for the transaction; "EKYC" and "2FA" are navigation modes
  const [navMode, setNavMode] = useState("AP"); // "AP" | "EKYC" | "2FA"

  const [form, setForm] = useState({
    aadhaar: "",
    mobile:  "",
    amount:  "",
    bank:    "",
  });

  const [showOtherBanks, setShowOtherBanks] = useState(false);
  const [searchTerm, setSearchTerm]         = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchOnboardingStatus = async () => {
    try {
      setOnboardingLoading(true);
      const res = await getOnboardingStatus(null, `?mid=${username}`);
      setOnboardingStatus(res?.responseCode || "NOT_REGISTERED");
      setEkycDone(res?.ekycDone === true);
    } catch {
      setOnboardingStatus("NOT_REGISTERED");
      setEkycDone(false);
    } finally {
      setOnboardingLoading(false);
    }
  };

  const fetchBanks = async () => {
    try {
      const res = await getBankIIN({authType: "BA" });
      if (Array.isArray(res)) {
        setBanks(res.map((b) => ({ iin: b.iin, name: b.description })));
      }
    } catch {}
  };

  useEffect(() => {
    fetchOnboardingStatus();
    fetchBanks();
  }, []);

  useEffect(() => {
    if (clearFormTrigger) {
      setForm({ aadhaar: "", mobile: "", amount: "", bank: "" });
      setSearchTerm("");
    }
  }, [clearFormTrigger]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "aadhaar") {
      const n = value.replace(/\D/g, "");
      if (n.length <= 12) setForm((p) => ({ ...p, aadhaar: n }));
      return;
    }
    if (name === "mobile") {
      const n = value.replace(/\D/g, "");
      if (n.length <= 10) setForm((p) => ({ ...p, mobile: n }));
      return;
    }
    if (name === "amount") {
      const n = value.replace(/\D/g, "");
      setForm((p) => ({ ...p, amount: n }));
      return;
    }
    setForm((p) => ({ ...p, [name]: value }));
  };

  const isFormValid =
    form.aadhaar.length === 12 &&
    form.mobile.length === 10 &&
    !!form.bank &&
    form.amount.trim() !== "" &&
    parseInt(form.amount) > 0;

  const handleQuickBankSelect = (bank) => {
    setForm((p) => ({ ...p, bank }));
    setShowOtherBanks(false);
  };

  const handleDropdownSelect = (bank) => {
    setForm((p) => ({ ...p, bank }));
    setSearchTerm(bank.name);
    setIsDropdownOpen(false);
  };

  const handlePreviousData = () => {
    if (!previousData) return;
    setForm((p) => ({ ...p, ...previousData, bank: previousData?.bank || "" }));
    if (previousData?.bank) setSearchTerm(previousData.bank.name || "");
  };

  // ── Onboarding gate screens ───────────────────────────────────────────────

  if (onboardingLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-slate-600 text-base">Checking status...</span>
      </div>
    );
  }

  if (onboardingStatus === "NOT_REGISTERED") {
    return (
      <VimoRegistration
        onRegistered={() => {}}
        onOtpVerified={fetchOnboardingStatus}
        onBack={null}
      />
    );
  }

  if (onboardingStatus === "OTP_PENDING") {
    return (
      <VimoRegistration
        initialPhase="otp"
        onRegistered={() => {}}
        onOtpVerified={fetchOnboardingStatus}
        onBack={null}
      />
    );
  }

  if (onboardingStatus === "EKYC_PENDING") {
    return (
      <VimoEkycComponent
        onBack={null}
        onSuccess={fetchOnboardingStatus}
      />
    );
  }

  if (onboardingStatus === "TWO_FA_PENDING") {
    return (
      <VimoTwoFactorAuth
        onBack={null}
        onSuccess={fetchOnboardingStatus}
      />
    );
  }

  // ── Manual EKYC / 2FA navigation ─────────────────────────────────────────

  if (navMode === "EKYC") {
    return (
      <VimoEkycFlow
        onBack={() => setNavMode("AP")}
        onSuccess={() => {
          fetchOnboardingStatus();
          setNavMode("AP");
        }}
      />
    );
  }

  if (navMode === "2FA") {
    return (
      <VimoTwoFactorAuth
        onBack={() => setNavMode("AP")}
        onSuccess={() => {
          fetchOnboardingStatus();
          setNavMode("AP");
        }}
      />
    );
  }

  // ── AP transaction form ───────────────────────────────────────────────────

  const highlightedBanks = bankMappings.map((item) => ({
    label: item.short,
    bank:  banks.find((b) => b.name?.toLowerCase() === item.full.toLowerCase()),
  })).filter((x) => x.bank);

  const safeSearch = (searchTerm || "").toLowerCase();
  const filteredDropdownBanks = banks.filter(
    (b) => b.name && b.name.toLowerCase().includes(safeSearch)
  );

  return (
    <div className="bg-[#f4f6fb]">
      <div className="ml-[12vw] -mt-14 font-semibold text-lg py-2">
        Aadhaar Pay 1
      </div>

      <div className="grid grid-cols-12 h-full">

        {/* LEFT — Form */}
        <div className="col-span-5 border-r px-5 py-4 flex flex-col justify-start h-full">

          <h3 className="font-bold text-xl mb-4">Aadhaar Pay</h3>

          {/* Navigation radios — only EKYC and 2FA; AP is always active */}
          <div className="grid grid-cols-2 gap-y-3 text-base font-semibold mb-6">
            {ekycDone ? (
              <div className="flex items-center gap-2">
                <CheckCircle2 size={17} className="text-green-600 shrink-0" />
                <div className="flex flex-col leading-tight">
                  <span className="text-base font-semibold text-slate-700">E-KYC</span>
                  <span className="text-xs font-semibold text-green-600">eKYC Completed</span>
                </div>
              </div>
            ) : (
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={navMode === "EKYC"}
                  onChange={() => setNavMode("EKYC")}
                />
                E-KYC
              </label>
            )}
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={navMode === "2FA"}
                onChange={() => setNavMode("2FA")}
              />
              2-Factor Auth
            </label>
          </div>

          <div className="space-y-4">

            <div>
              <label className="block mb-2 text-base font-semibold">Aadhaar Number</label>
              <input
                name="aadhaar"
                type="text"
                value={form.aadhaar}
                onChange={handleChange}
                maxLength={12}
                inputMode="numeric"
                placeholder="Aadhaar Number"
                className="w-full h-12 border-2 rounded-lg px-4 text-base font-bold"
              />
              {form.aadhaar && form.aadhaar.length < 12 && (
                <p className="text-red-500 text-sm mt-1">Aadhaar must be 12 digits</p>
              )}
            </div>

            <div>
              <label className="block mb-2 text-base font-semibold">Mobile Number</label>
              <input
                name="mobile"
                value={form.mobile}
                onChange={handleChange}
                maxLength={10}
                inputMode="numeric"
                placeholder="Mobile Number"
                className="w-full h-12 border-2 rounded-lg px-4 text-base font-bold"
              />
              {form.mobile && form.mobile.length < 10 && (
                <p className="text-red-500 text-sm mt-1">Mobile must be 10 digits</p>
              )}
            </div>

            <div>
              <label className="block mb-2 text-base font-semibold">Transaction Amount</label>
              <input
                name="amount"
                value={form.amount}
                onChange={handleChange}
                inputMode="numeric"
                placeholder="Transaction Amount"
                className="w-full h-12 border-2 rounded-lg px-4 text-base"
              />
            </div>

          </div>

          <div className="mt-5">
            <div className="grid grid-cols-2 gap-3">

              <button
                onClick={() => onSubmit({ ...form, txnType: "AP" })}
                disabled={loading || onboardingStatus !== "READY" || !isFormValid || processing}
                className="h-12 bg-green-600 text-white rounded-lg text-base font-bold flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Fingerprint size={20} />
                {loading ? "Capturing..." : "Capture & Pay"}
              </button>

              <button
                type="button"
                onClick={handlePreviousData}
                disabled={!previousData}
                className="h-12 bg-slate-700 text-white rounded-lg text-base font-bold"
              >
                Previous
              </button>

            </div>
          </div>

        </div>

        {/* RIGHT — Bank selection */}
        <div className="col-span-7 px-4 py-4">

          <h3 className="font-bold text-xl mb-4">Bank Name :</h3>

          <div className="grid grid-cols-5 gap-3">

            {highlightedBanks.map(({ label, bank }, index) => (
              <button
                key={bank.iin}
                type="button"
                onClick={() => handleQuickBankSelect(bank)}
                className={`
                  h-16 rounded-xl text-white font-bold text-sm shadow leading-tight
                  ${BANK_COLORS[index]}
                  ${form.bank?.iin === bank.iin ? "ring-4 ring-green-300" : ""}
                `}
              >
                <div className="text-[16px]">{label}</div>
                <div className="text-[12px] mt-1 opacity-90">{bank.name}</div>
              </button>
            ))}

            <button
              onClick={() => {
                setShowOtherBanks(!showOtherBanks);
                if (!showOtherBanks && form.bank) setSearchTerm(form.bank.name || "");
              }}
              className="h-16 rounded-xl bg-blue-900 text-white font-bold text-sm shadow"
            >
              OTHER BANKS
            </button>

          </div>

          {showOtherBanks && (
            <div className="mt-4 relative" ref={dropdownRef}>
              <input
                type="text"
                placeholder="Search and select bank..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setIsDropdownOpen(true); }}
                onFocus={() => setIsDropdownOpen(true)}
                className="w-full h-12 border-2 rounded-lg px-4 text-base focus:border-indigo-600 focus:outline-none bg-white"
              />
              {isDropdownOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-2xl max-h-60 overflow-y-auto">
                  {filteredDropdownBanks.length > 0 ? (
                    filteredDropdownBanks.map((b) => (
                      <div
                        key={b.iin}
                        onClick={() => handleDropdownSelect(b)}
                        className="px-4 py-3 hover:bg-slate-100 cursor-pointer text-slate-700 border-b last:border-b-0 text-sm font-medium"
                      >
                        {b.name}
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-slate-500 text-sm">No banks found</div>
                  )}
                </div>
              )}
            </div>
          )}

        </div>

      </div>
    </div>
  );
}
